import { signIn } from "next-auth/react";
import { useForm, type SubmitHandler } from "react-hook-form";
import { Button } from "../ui/button";
import { SiGoogle, SiSimplelogin } from "@icons-pack/react-simple-icons";
import { Input } from "../ui/input";
import Link from "next/link";
import { useParams } from "next/navigation";
import { useEffect } from "react";
import { callServerActionSafely } from "~/lib/client/call-server-action-safely";
import { getClientTrackingDebugCode } from "~/lib/client/test-diagnostics";
import { pushGA4Event } from "~/lib/ga4-browser";
import { trackEventAction } from "~/server/actions/tracking-actions";
import { shouldTrackClientEventOnce } from "~/lib/client/tracking-once";
import { type RouterOutput } from "~/server/api/root";
import {
  TherapyRelationshipType,
  TherapySessionPurpose,
} from "~/prisma/browser";
import {
  parseAsInteger,
  parseAsIsoDateTime,
  parseAsStringEnum,
  useQueryState,
  useQueryStates,
} from "nuqs";
import { capturePostHogEvent } from "~/lib/posthog/client";

type Inputs = {
  email: string;
};

export const StepLoginAuthorised = ({
  therapist,
  attributionBridgeId,
  sessionPurpose,
  therapyType,
}: {
  therapist: NonNullable<RouterOutput["therapistRouter"]["getTherapist"]>;
  attributionBridgeId?: string;
  sessionPurpose: TherapySessionPurpose;
  therapyType: TherapyRelationshipType;
}) => {
  const [, setBookingParams] = useQueryStates({
    step: parseAsInteger,
    sessionPurpose: parseAsStringEnum<TherapySessionPurpose>(
      Object.values(TherapySessionPurpose),
    ),
  });

  useEffect(() => {
    const itemPrice = therapist.defaultPrice ?? 0;
    const itemName = therapist.user.name ?? "";
    const trackingKey = `add_to_cart:${window.location.pathname}:${therapist.id}`;

    const advanceToPayment = () => {
      void setBookingParams({
        step: 2,
        sessionPurpose:
          sessionPurpose === TherapySessionPurpose.INTRO_CALL
            ? TherapySessionPurpose.INTRO_CALL
            : null,
      });
    };

    if (sessionPurpose === TherapySessionPurpose.INTRO_CALL) {
      advanceToPayment();
      return;
    }

    const shouldTrack = shouldTrackClientEventOnce(trackingKey);

    if (!shouldTrack) {
      advanceToPayment();
      return;
    }

    pushGA4Event("add_to_cart", {
      currency: "EUR",
      value: itemPrice,
      items: [
        {
          item_id: therapist.id,
          item_name: itemName,
          price: itemPrice,
          quantity: 1,
        },
      ],
    });

    advanceToPayment();

    void callServerActionSafely(
      "track_add_to_cart",
      () =>
        trackEventAction(
          {
            event: "add_to_cart",
            data: {
              value: itemPrice,
              therapistId: therapist.id,
              therapistName: itemName,
              therapyType,
              sourceUrl: window.location.pathname,
            },
          },
          {
            attributionBridgeId,
            debugTestCode: getClientTrackingDebugCode(),
          },
        ),
      { therapistId: therapist.id, sourceUrl: window.location.pathname },
      { failureReporting: "warning" },
    );
  }, [
    therapist,
    sessionPurpose,
    therapyType,
    attributionBridgeId,
    setBookingParams,
  ]);

  return (
    <div className="p-4">
      <div className="space-y-4">
        <div>
          <h3 className="mb-2 text-lg font-semibold">Preusmeravanje...</h3>
          <p className="mb-4 text-sm text-muted-foreground">
            Automatski nastavljamo sa rezervacijom.
          </p>
        </div>
      </div>
    </div>
  );
};

export const URL_IDENTIFIER_PATIENT = "type=patient";

export function LoginInner({ callbackUrl }: { callbackUrl: string }) {
  const {
    register,
    handleSubmit,
    formState: { isValid, isSubmitting },
  } = useForm<Inputs>({ mode: "onChange" });
  const onSubmit: SubmitHandler<Inputs> = async ({ email }) => {
    await signIn("http-email", {
      callbackUrl,
      email,
    });

    capturePostHogEvent("login", { provider: "email" });
  };

  const onGoogleLogin = async () => {
    await signIn("google", {
      callbackUrl,
    });

    capturePostHogEvent("login", { provider: "google" });
  };

  return (
    <div className="w-full p-5 md:max-w-md md:p-6" data-testid="step-login">
      <div className="mb-5 space-y-1">
        <h2 className="text-xl font-semibold tracking-tight">
          Nastavi zakazivanje
        </h2>
        <p className="text-sm text-muted-foreground">
          Nastavi da bi potvrdio/la izabrani termin. Potvrdu ti šaljemo na
          email.
        </p>
      </div>
      <div className="grid gap-4">
        <Button
          onClick={onGoogleLogin}
          variant="default"
          className="w-full"
          icon={SiGoogle}
        >
          Nastavi preko Google naloga
        </Button>

        <div className="relative">
          <div className="absolute inset-0 flex items-center">
            <span className="w-full border-t" />
          </div>
          <div className="relative flex justify-center text-xs uppercase">
            <span className="bg-background px-3 text-muted-foreground">
              Ili nastavi sa
            </span>
          </div>
        </div>
        <form onSubmit={handleSubmit(onSubmit)} className="grid gap-2">
          <Input
            type="email"
            placeholder="Email adresa"
            {...register("email", {
              required: true,
              pattern: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
            })}
          />
          <Button
            variant="outline"
            className="w-full"
            type="submit"
            disabled={!isValid || isSubmitting}
            icon={SiSimplelogin}
          >
            Pošalji link za prijavu
          </Button>
          <p className="text-center text-xs text-muted-foreground">
            Poslaćemo ti link za prijavu. Nema lozinke.
          </p>
        </form>
        <p className="text-center text-xs leading-relaxed text-muted-foreground">
          Ako nastaviš, prihvataš{" "}
          <Link href="/legal/uslovi-koriscenja" className="underline">
            uslove korišćenja
          </Link>
          . Tvoje podatke koristimo u skladu sa{" "}
          <Link href="/legal/politika-privatnosti" className="underline">
            politikom privatnosti
          </Link>
          .
        </p>
      </div>
    </div>
  );
}

export default function StepLogin({
  therapist,
  attributionBridgeId,
  sessionPurpose,
  therapyType,
}: {
  therapist: NonNullable<RouterOutput["therapistRouter"]["getTherapist"]>;
  attributionBridgeId?: string;
  sessionPurpose: TherapySessionPurpose;
  therapyType: TherapyRelationshipType;
}) {
  const [sessionDate] = useQueryState("date", parseAsIsoDateTime);
  const [sourceSurface] = useQueryState(
    "sourceSurface",
    parseAsStringEnum(["therapist_profile", "free_intro_call_directory"]),
  );
  const params = useParams<{ therapistSlug: string }>();

  useEffect(() => {
    if (sessionPurpose === TherapySessionPurpose.INTRO_CALL) {
      return;
    }

    const itemPrice = therapist.defaultPrice ?? 0;
    const itemName = therapist.user.name ?? "";
    const trackingKey = `add_to_cart:${window.location.pathname}:${therapist.id}`;

    if (!shouldTrackClientEventOnce(trackingKey)) {
      return;
    }

    pushGA4Event("add_to_cart", {
      currency: "EUR",
      value: itemPrice,
      items: [
        {
          item_id: therapist.id,
          item_name: itemName,
          price: itemPrice,
          quantity: 1,
        },
      ],
    });

    void callServerActionSafely(
      "track_add_to_cart",
      () =>
        trackEventAction(
          {
            event: "add_to_cart",
            data: {
              value: itemPrice,
              therapistId: therapist.id,
              therapistName: itemName,
              therapyType,
              sourceUrl: window.location.pathname,
            },
          },
          {
            attributionBridgeId,
            debugTestCode: getClientTrackingDebugCode(),
          },
        ),
      { therapistId: therapist.id, sourceUrl: window.location.pathname },
      { failureReporting: "warning" },
    );
  }, [therapist, attributionBridgeId, sessionPurpose, therapyType]);

  if (!sessionDate) {
    return <div>Učitavanje...</div>;
  }

  const callbackParams = new URLSearchParams({
    date: sessionDate.toISOString(),
    step: "2",
  });
  callbackParams.set(
    "therapyType",
    sessionPurpose === TherapySessionPurpose.INTRO_CALL
      ? TherapyRelationshipType.INDIVIDUAL
      : therapyType,
  );
  if (sessionPurpose === TherapySessionPurpose.INTRO_CALL) {
    callbackParams.set("sessionPurpose", TherapySessionPurpose.INTRO_CALL);
  }
  if (sourceSurface) {
    callbackParams.set("sourceSurface", sourceSurface);
  }
  callbackParams.set("type", "patient");
  const callbackUrlBase = `/terapeuti/${params.therapistSlug}/book?${callbackParams.toString()}`;
  const callbackUrl = attributionBridgeId
    ? `${callbackUrlBase}&ab=${encodeURIComponent(attributionBridgeId)}`
    : callbackUrlBase;

  return <LoginInner callbackUrl={callbackUrl} />;
}
